/**
* Single source of truth for Thai government fees & processing times.
*
* RULES
* - Every entry MUST carry a primary source URL and the date a human verified it.
* - Fees that we cannot verify from a primary source are written as `variable`
* with guidance to check with the authority — never as a hard number.
* - Our own service prices do NOT belong in this file.
*/

export type OfficialFee = {
  /** Stable key used across content modules. */
  key: string;
  /** Thai label of the fee. */
  labelTh: string;
  /** Human-readable amount, already formatted (may be a range). */
  amountTh: string;
  /** Official processing time, as published by the authority. */
  timeTh: string;
  /** Authority that collects the fee. */
  authorityTh: string;
  sourceLabel: string;
  sourceUrl: string;
  /** ISO date a human last checked the source. */
  verifiedOnISO: string;
  verifiedOnTh: string;
  /** True when the amount depends on country/office and cannot be fixed. */
  variable?: boolean;
};

const VERIFIED_ISO = "2026-07-31";
const VERIFIED_TH = "31 กรกฎาคม 2569";

export const OFFICIAL_FEES = {
  /**
   * Legalisation (นิติกรณ์) at the Department of Consular Affairs.
   * Published rates: 200 THB per legalisation stamp for the normal service
   * (document returned within 3 working days, counting the filing day) and
   * 400 THB per stamp for the express service (same-day collection).
   */
  mfaLegalisation: {
    key: "mfaLegalisation",
    labelTh: "ค่าธรรมเนียมนิติกรณ์ (รับรองเอกสาร)",
    amountTh: "200 บาท/แผ่นประทับนิติกรณ์ (แบบปกติ) · 400 บาท/แผ่น (แบบด่วน)",
    timeTh: "แบบปกติรับเอกสารภายใน 3 วันทำการ (นับรวมวันยื่น) · แบบด่วนรับภายในวันเดียวกัน",
    authorityTh: "กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ",
    sourceLabel: "กรมการกงสุล — ขั้นตอนและค่าธรรมเนียมการรับรองนิติกรณ์เอกสาร",
    sourceUrl: "https://consular.mfa.go.th/th/page/legalization",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Police clearance certificate application fee. */
  policeClearance: {
    key: "policeClearance",
    labelTh: "ค่าธรรมเนียมตรวจสอบประวัติอาชญากรรม (Police Clearance Certificate)",
    amountTh: "สอบถามค่าบริการ",
    timeTh: "ระยะเวลาพิจารณาขึ้นกับผลการตรวจสอบและปริมาณคำร้องในแต่ละช่วง — โปรดตรวจสอบสถานะกับกองทะเบียนประวัติอาชญากร",
    authorityTh: "กองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ",
    sourceLabel: "กองทะเบียนประวัติอาชญากร สำนักงานตำรวจแห่งชาติ",
    sourceUrl: "https://pcscenter.sb.police.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Destination-embassy legalisation fees: never a fixed number. */
  embassyLegalisation: {
    key: "embassyLegalisation",
    labelTh: "ค่าธรรมเนียมรับรองเอกสารของสถานทูต/สถานกงสุลปลายทาง",
    amountTh: "แตกต่างกันในแต่ละประเทศ และมีการปรับอัตราเป็นระยะ",
    timeTh: "ขึ้นกับระเบียบของแต่ละสถานทูต",
    authorityTh: "สถานเอกอัครราชทูต/สถานกงสุลของประเทศปลายทาง",
    sourceLabel: "ตรวจสอบอัตราล่าสุดจากเว็บไซต์สถานทูตของประเทศปลายทางโดยตรง",
    sourceUrl: "https://consular.mfa.go.th/th/page/legalization",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
    variable: true,
  },
  /** TM.7 — application to extend temporary stay (Immigration Bureau). */
  immigrationExtension: {
    key: "immigrationExtension",
    labelTh: "ค่าธรรมเนียมคำขออยู่ต่อในราชอาณาจักรเป็นการชั่วคราว (แบบ ตม.7)",
    amountTh: "สอบถามค่าบริการ",
    timeTh: "ยื่นก่อนวันอนุญาตเดิมสิ้นสุด · ระยะเวลาพิจารณาขึ้นกับดุลพินิจของพนักงานเจ้าหน้าที่และปริมาณคำขอของแต่ละด่าน",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง สำนักงานตำรวจแห่งชาติ",
    sourceLabel: "ตารางอัตราค่าธรรมเนียม — สำนักงานตรวจคนเข้าเมือง",
    sourceUrl: "https://www.samutprakanimmigration.go.th/immigration-fees-2/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** TM.8 — re-entry permit. */
  reEntryPermit: {
    key: "reEntryPermit",
    labelTh: "ค่าธรรมเนียมสงวนสิทธิ์การกลับเข้ามาในราชอาณาจักร (แบบ ตม.8 / Re-Entry Permit)",
    amountTh: "แบบใช้ได้ครั้งเดียว (Single) 1,000 บาท · แบบใช้ได้หลายครั้ง (Multiple) 3,800 บาท",
    timeTh: "ยื่นก่อนเดินทางออกนอกราชอาณาจักร · โดยทั่วไปดำเนินการภายในวันเดียวที่ด่าน ตม. หรือสนามบิน",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง สำนักงานตำรวจแห่งชาติ",
    sourceLabel: "สำนักงานตรวจคนเข้าเมือง — Re-Entry Permit (ตม.8)",
    sourceUrl: "https://www.immigration.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Residence certificate (PR) issuance fee. */
  residenceCertificate: {
    key: "residenceCertificate",
    labelTh: "ค่าธรรมเนียมใบสำคัญถิ่นที่อยู่ (ตม.16 — Permanent Residence)",
    amountTh: "กรณีลงทุน/ทำงาน/ผู้เชี่ยวชาญ สอบถามค่าบริการ · กรณีสมรสกับคนสัญชาติไทยหรือเป็นบุตร/คู่สมรสของผู้มีถิ่นที่อยู่ สอบถามค่าบริการ",
    timeTh: "ชำระเมื่อคำขอได้รับอนุมัติ · กระบวนการพิจารณาใช้เวลานานและมีโควตารายปีตามสัญชาติ",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง สำนักงานตำรวจแห่งชาติ",
    sourceLabel: "ตารางอัตราค่าธรรมเนียม — สำนักงานตรวจคนเข้าเมือง",
    sourceUrl: "https://www.samutprakanimmigration.go.th/immigration-fees-2/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Work permit fees under the 2560 Emergency Decree ministerial regulations. */
  workPermit: {
    key: "workPermit",
    labelTh: "ค่าธรรมเนียมใบอนุญาตทำงานของคนต่างด้าว",
    amountTh: "ค่าคำขอ 100 บาท · ค่าธรรมเนียมใบอนุญาต: ไม่เกิน 3 เดือน 750 บาท · 3–6 เดือน 1,500 บาท · 6–12 เดือน 3,000 บาท · 2 ปี 6,000 บาท",
    timeTh: "ขึ้นกับความครบถ้วนของเอกสารนายจ้างและคิวของสำนักงานจัดหางานพื้นที่",
    authorityTh: "กรมการจัดหางาน กระทรวงแรงงาน",
    sourceLabel: "กรมการจัดหางาน — การขออนุญาตจ้างคนต่างด้าวเข้าทำงาน (มาตรา 59)",
    sourceUrl: "https://www.doe.go.th/prd/alien",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** DBD company registration fees — Ministerial Regulation B.E. 2563. */
  companyRegistration: {
    key: "companyRegistration",
    labelTh: "ค่าธรรมเนียมจดทะเบียนธุรกิจ (กรมพัฒนาธุรกิจการค้า)",
    amountTh: "จดทะเบียนห้างหุ้นส่วน 1,000 บาท · หนังสือบริคณห์สนธิบริษัทจำกัด 500 บาท · จดทะเบียนบริษัทจำกัด 5,000 บาท · จดทะเบียนแก้ไขเพิ่มเติม ครั้งละ 500 บาท · ใบสำคัญแสดงการจดทะเบียน ฉบับละ 100 บาท · หนังสือรับรอง รายการละ 40 บาท",
    timeTh: "จดทะเบียนหนังสือบริคณห์สนธิและจัดตั้งบริษัทภายในวันเดียวได้ หากดำเนินการครบทุกขั้นตอนในวันเดียวกัน",
    authorityTh: "กรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์",
    sourceLabel: "กฎกระทรวงกำหนดอัตราค่าธรรมเนียมฯ เกี่ยวกับห้างหุ้นส่วนและบริษัทจำกัด พ.ศ. 2563 (ราชกิจจานุเบกษา 29 ธ.ค. 2563)",
    sourceUrl: "https://www.dbd.go.th/manual/1469",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Court filing fee — Table 1 annexed to the Civil Procedure Code (as amended No. 24, B.E. 2551). */
  courtFiling: {
    key: "courtFiling",
    labelTh: "ค่าขึ้นศาลคดีแพ่ง (ตาราง 1 ท้ายประมวลกฎหมายวิธีพิจารณาความแพ่ง)",
    amountTh:
      "คดีมีทุนทรัพย์ทั่วไป 2% ของทุนทรัพย์ ไม่เกิน 200,000 บาท (เฉพาะส่วนที่ไม่เกิน 50 ล้านบาท) และ 0.1% สำหรับส่วนที่เกิน 50 ล้านบาท · คดีบังคับจำนอง 1% ไม่เกิน 100,000 บาท · คดีมโนสาเร่ที่มีทุนทรัพย์ 2% ไม่เกิน 1,000 บาท · คดีไม่มีทุนทรัพย์ เรื่องละ 200 บาท",
    timeTh: "ชำระในวันยื่นคำฟ้อง · ระยะเวลาพิจารณาคดีขึ้นกับศาลและประเภทคดี",
    authorityTh: "ศาลยุติธรรม",
    sourceLabel: "ตาราง 1 ค่าธรรมเนียมศาล ท้าย ป.วิ.พ. (แก้ไขเพิ่มเติมฉบับที่ 24 พ.ศ. 2551)",
    sourceUrl: "https://www.drthawip.com/civilprocedurecode/084",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Legal Execution Department fees — Table 5, as amended by Act No. 33 (2568). */
  enforcementFees: {
    key: "enforcementFees",
    labelTh: "ค่าธรรมเนียมเจ้าพนักงานบังคับคดี (ตาราง 5 ท้าย ป.วิ.พ.)",
    amountTh:
      "ขายทอดตลาดหรือจำหน่ายโดยวิธีอื่นซึ่งทรัพย์สินที่ยึดหรืออายัด 2% ของจำนวนเงินที่ขายหรือจำหน่าย · จ่ายเงินที่ยึดหรืออายัดแก่เจ้าหนี้ 1% · ยึดทรัพย์สินซึ่งไม่ใช่ตัวเงินแล้วไม่มีการขายหรือจำหน่าย ไม่เรียกเก็บ",
    timeTh: "อัตราใหม่มีผลตั้งแต่ 10 กันยายน 2568 ตาม พ.ร.บ. แก้ไขเพิ่มเติม ป.วิ.พ. (ฉบับที่ 33) พ.ศ. 2568",
    authorityTh: "กรมบังคับคดี กระทรวงยุติธรรม",
    sourceLabel: "กรมบังคับคดี — ลดค่าธรรมเนียมบังคับคดี มีผล 10 กันยายน 2568",
    sourceUrl:
      "https://gcc.go.th/2025/09/13/%E0%B8%81%E0%B8%A3%E0%B8%A1%E0%B8%9A%E0%B8%B1%E0%B8%87%E0%B8%84%E0%B8%B1%E0%B8%9A%E0%B8%84%E0%B8%94%E0%B8%B5-%E0%B8%A5%E0%B8%94%E0%B8%84%E0%B9%88%E0%B8%B2%E0%B8%98%E0%B8%A3%E0%B8%A3%E0%B8%A1%E0%B9%80/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** DIP trademark fees — published fee table, Trademark Act ministerial regulation. */
  dipTrademark: {
    key: "dipTrademark",
    labelTh: "ค่าธรรมเนียมเครื่องหมายการค้าในประเทศ (กรมทรัพย์สินทางปัญญา)",
    amountTh:
      "คำขอจดทะเบียน (ก.01) สินค้า/บริการแต่ละจำพวก 1–5 อย่าง อย่างละ 1,000 บาท · เกิน 5 อย่าง จำพวกละ 9,000 บาท · ค่าธรรมเนียมการจดทะเบียน อย่างละ 600 บาท (เกิน 5 อย่าง จำพวกละ 5,400 บาท) · ต่ออายุ อย่างละ 2,000 บาท (เกิน 5 อย่าง จำพวกละ 18,000 บาท) · คำคัดค้าน ฉบับละ 2,000 บาท · คำขอโอน/รับมรดกสิทธิ คำขอละ 2,000 บาท · คำขอจดทะเบียนสัญญาอนุญาตให้ใช้เครื่องหมาย คำขอละ 1,000 บาท",
    timeTh:
      "ประกาศโฆษณาเพื่อให้คัดค้านได้ภายใน 60 วันนับแต่วันประกาศ · ระยะเวลาตรวจสอบขึ้นกับปริมาณคำขอและการออกคำสั่งของนายทะเบียน · ต่ออายุยื่นได้ภายใน 3 เดือนก่อนสิ้นอายุ หากยื่นหลังสิ้นอายุแต่ไม่เกิน 6 เดือน มีค่าปรับเพิ่ม 20% ของค่าต่ออายุ",
    authorityTh: "กรมทรัพย์สินทางปัญญา กระทรวงพาณิชย์",
    sourceLabel: "กรมทรัพย์สินทางปัญญา — ค่าธรรมเนียมเครื่องหมายการค้าในประเทศ",
    sourceUrl: "https://www.ipthailand.go.th/th/trademark-006.html",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Patent / petty patent fees: published per fee table; do not hard-code a single number. */
  dipPatent: {
    key: "dipPatent",
    labelTh: "ค่าธรรมเนียมสิทธิบัตร/อนุสิทธิบัตร (กรมทรัพย์สินทางปัญญา)",
    amountTh:
      "คิดตามตารางค่าธรรมเนียมท้าย พ.ร.บ. สิทธิบัตร ซึ่งแยกรายการเป็นค่าคำขอ ค่าประกาศโฆษณา ค่าคำขอให้ตรวจสอบการประดิษฐ์ ค่ารับจดทะเบียน และค่าธรรมเนียมรายปี — จำนวนต่างกันตามประเภทและปีที่ชำระ",
    timeTh:
      "สิทธิบัตรการประดิษฐ์คุ้มครอง 20 ปีนับแต่วันขอรับสิทธิบัตร · สิทธิบัตรการออกแบบผลิตภัณฑ์ 10 ปี · อนุสิทธิบัตร 6 ปี ต่ออายุได้ 2 ครั้ง ครั้งละ 2 ปี (รวมไม่เกิน 10 ปี)",
    authorityTh: "กรมทรัพย์สินทางปัญญา กระทรวงพาณิชย์",
    sourceLabel: "กรมทรัพย์สินทางปัญญา — ค่าธรรมเนียมสิทธิบัตร",
    sourceUrl: "https://www.ipthailand.go.th/th/patent-002.html",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
    variable: true,
  },
  /** Copyright notification at DIP — no government fee is collected. */
  dipCopyright: {
    key: "dipCopyright",
    labelTh: "การแจ้งข้อมูลลิขสิทธิ์ (กรมทรัพย์สินทางปัญญา)",
    amountTh:
      "ไม่มีค่าธรรมเนียมราชการ — ลิขสิทธิ์ได้รับความคุ้มครองโดยอัตโนมัติเมื่อสร้างสรรค์ผลงาน การแจ้งข้อมูลเป็นเพียงการบันทึกหลักฐานต่อกรมทรัพย์สินทางปัญญา ไม่ใช่การจดทะเบียนสิทธิ",
    timeTh:
      "ยื่นได้ทั้งทางออนไลน์และที่กรมฯ · ระยะเวลาออกหนังสือรับรองการแจ้งข้อมูลขึ้นกับความครบถ้วนของเอกสารและปริมาณคำขอ",
    authorityTh: "กรมทรัพย์สินทางปัญญา กระทรวงพาณิชย์",
    sourceLabel: "กรมทรัพย์สินทางปัญญา — การแจ้งข้อมูลลิขสิทธิ์",
    sourceUrl: "https://www.ipthailand.go.th/th/copyright-001.html",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** DBD annual filing duties — deadlines are the verifiable facts, not a flat fee. */
  dbdFinancialStatement: {
    key: "dbdFinancialStatement",
    labelTh: "หน้าที่ยื่นงบการเงินและบัญชีรายชื่อผู้ถือหุ้นรายปี (กรมพัฒนาธุรกิจการค้า)",
    amountTh:
      "การยื่นงบการเงินผ่านระบบ DBD e-Filing ไม่มีค่าธรรมเนียมยื่น · ค่าธรรมเนียมจะเกิดเฉพาะกรณีขอหนังสือรับรอง/คัดสำเนาเอกสาร และกรณีเปรียบเทียบปรับเมื่อยื่นล่าช้าตามอัตราที่ทางราชการกำหนด",
    timeTh:
      "บริษัทจำกัดต้องจัดประชุมสามัญผู้ถือหุ้นเพื่ออนุมัติงบการเงินภายใน 4 เดือนนับแต่วันสิ้นรอบปีบัญชี · ยื่นงบการเงินภายใน 1 เดือนนับแต่วันที่ที่ประชุมอนุมัติ · ยื่นบัญชีรายชื่อผู้ถือหุ้น (บอจ.5) ภายใน 14 วันนับแต่วันประชุมสามัญ",
    authorityTh: "กรมพัฒนาธุรกิจการค้า กระทรวงพาณิชย์",
    sourceLabel: "กรมพัฒนาธุรกิจการค้า — การนำส่งงบการเงินและบัญชีรายชื่อผู้ถือหุ้น",
    sourceUrl: "https://www.dbd.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** VAT registration (ภ.พ.01) — threshold + no application fee. */
  vatRegistration: {
    key: "vatRegistration",
    labelTh: "การจดทะเบียนภาษีมูลค่าเพิ่ม (แบบ ภ.พ.01)",
    amountTh:
      "ไม่มีค่าธรรมเนียมในการยื่นคำขอจดทะเบียน · ผู้ประกอบการที่มีรายรับจากการขายสินค้าหรือให้บริการเกิน 1.8 ล้านบาทต่อปี มีหน้าที่ต้องจดทะเบียน",
    timeTh:
      "ต้องยื่นคำขอภายใน 30 วันนับแต่วันที่มีรายรับเกินเกณฑ์ (หรือก่อนเริ่มประกอบกิจการกรณีจดทะเบียนโดยสมัครใจ) · เมื่อเป็นผู้ประกอบการจดทะเบียนแล้ว ต้องยื่นแบบ ภ.พ.30 รายเดือนภายในวันที่ 15 ของเดือนถัดไป",
    authorityTh: "กรมสรรพากร กระทรวงการคลัง",
    sourceLabel: "กรมสรรพากร — การจดทะเบียนภาษีมูลค่าเพิ่ม",
    sourceUrl: "https://www.rd.go.th/7058.html",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Marriage / civil-status registration at the district office. */
  marriageRegistration: {
    key: "marriageRegistration",
    labelTh: "ค่าธรรมเนียมจดทะเบียนสมรสและทะเบียนครอบครัว (สำนักทะเบียน/เขต/อำเภอ)",
    amountTh:
      "จดทะเบียนที่สำนักทะเบียนในเวลาราชการ ไม่เสียค่าธรรมเนียม · กรณีขอให้นายทะเบียนออกไปจดทะเบียนนอกสำนักทะเบียน มีค่าธรรมเนียมตามที่กฎกระทรวงกำหนด และผู้ร้องต้องจัดหาพาหนะรับส่งเจ้าหน้าที่",
    timeTh:
      "โดยทั่วไปดำเนินการเสร็จภายในวันเดียวเมื่อเอกสารครบถ้วนและคู่สมรสมาแสดงตนพร้อมพยาน · เอกสารต่างประเทศต้องแปลเป็นภาษาไทยและผ่านการรับรองก่อนยื่น",
    authorityTh: "สำนักทะเบียนอำเภอ/สำนักงานเขต กรมการปกครอง กระทรวงมหาดไทย",
    sourceLabel: "สำนักบริหารการทะเบียน กรมการปกครอง — งานทะเบียนครอบครัว",
    sourceUrl: "https://www.bora.dopa.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Certified copies of civil-registration records. */
  civilRegistrationCopies: {
    key: "civilRegistrationCopies",
    labelTh: "ค่าธรรมเนียมคัดและรับรองสำเนารายการทะเบียนราษฎร",
    amountTh: "ฉบับละ 10 บาท ตามอัตราที่กฎกระทรวงกำหนด (เอกสารเช่น สูติบัตร มรณบัตร ทะเบียนบ้าน ทะเบียนสมรส)",
    timeTh: "โดยทั่วไปได้รับเอกสารภายในวันเดียวที่สำนักทะเบียน หากรายการทะเบียนอยู่ในระบบและเอกสารแสดงตนครบถ้วน",
    authorityTh: "สำนักทะเบียนอำเภอ/สำนักงานเขต กรมการปกครอง กระทรวงมหาดไทย",
    sourceLabel: "สำนักบริหารการทะเบียน กรมการปกครอง",
    sourceUrl: "https://www.bora.dopa.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
  },
  /** Land transactions — percentages are set by law but the tax mix varies per case. */
  landTransfer: {
    key: "landTransfer",
    labelTh: "ค่าธรรมเนียมและภาษีในการจดทะเบียนสิทธิและนิติกรรมที่ดิน",
    amountTh:
      "ค่าธรรมเนียมจดทะเบียนโอนกรรมสิทธิ์คิดในอัตราร้อยละ 2 ของราคาประเมินทุนทรัพย์ · นอกจากนี้ยังมีอากรแสตมป์ ภาษีธุรกิจเฉพาะ และภาษีเงินได้หัก ณ ที่จ่าย ซึ่งอัตราและกรณีที่ต้องเสียแตกต่างกันตามระยะเวลาถือครองและสถานะของผู้โอน · บางช่วงเวลามีมาตรการลดค่าธรรมเนียมตามประกาศกระทรวงมหาดไทย",
    timeTh:
      "โดยทั่วไปจดทะเบียนเสร็จภายในวันเดียวที่สำนักงานที่ดิน หากเอกสารครบถ้วนและไม่มีข้อโต้แย้งเรื่องแนวเขต · กรณีนิติบุคคลหรือคนต่างชาติต้องเตรียมเอกสารรับรองเพิ่มและอาจใช้เวลามากกว่านั้น",
    authorityTh: "กรมที่ดิน กระทรวงมหาดไทย",
    sourceLabel: "กรมที่ดิน — ค่าธรรมเนียมและภาษีเกี่ยวกับการจดทะเบียนสิทธิและนิติกรรม",
    sourceUrl: "https://www.dol.go.th/",
    verifiedOnISO: VERIFIED_ISO,
    verifiedOnTh: VERIFIED_TH,
    variable: true,
  },
} as const satisfies Record<string, OfficialFee>;


export type OfficialFeeKey = keyof typeof OFFICIAL_FEES;

/** Sentence used wherever a government fee is quoted inline. */
export function feeSentenceTh(key: OfficialFeeKey): string {
  const f = OFFICIAL_FEES[key];
  return `${f.labelTh}: ${f.amountTh} (${f.authorityTh}) — ข้อมูล ณ ${f.verifiedOnTh} โปรดตรวจสอบอัตราล่าสุดกับหน่วยงานก่อนดำเนินการ`;
}

export const FEE_DISCLAIMER_TH =
  `ค่าธรรมเนียมราชการข้างต้นเป็นข้อมูล ณ ${VERIFIED_TH} หน่วยงานอาจปรับอัตราหรือระยะเวลาได้โดยไม่แจ้งล่วงหน้า โปรดตรวจสอบกับหน่วยงานเจ้าของเรื่องอีกครั้งก่อนยื่นเอกสาร`;
